import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import sectionStyles from './ScenarioGenerator.module.css';
import styles from './BgmFreeTrackPicker.module.css';
import { formatAudioDuration } from '../../common/AudioPlayButton';
import { AudioTrackTable, AudioTrackTableMessage, type AudioTrackRow } from '../../common/AudioTrackTable';
import { useAudioCatalog } from '../../../features/audioCatalog/hooks';
import type { AudioCatalogItem } from '../../../features/audioCatalog/api';
import { brandConfig } from '../../../config/brands';

/**
 * BGM セクション「フリー楽曲」タブ (docs/features/free-bgm-catalog-plan.md)。
 *
 * 候補は運営提供カタログ (`audio_catalog_items`) のうち用途が BGM のもの。楽曲は多くなる前提
 * なので、セクション内には「選択中の曲 + 楽曲を選ぶボタン」だけを置き、一覧は**モーダル**で出す
 * (2026-09-24 ユーザー決定)。モーダルは曲名で絞り込みでき、2 層 (共通 = 運営提供 / パートナー独自)
 * を同じ一覧に出して両方あるときだけ見出しで区切る (パートナー独自を先に出す)。
 * 試聴は署名付き preview_url をそのまま鳴らす。行に出すのは曲名と尺だけ
 * (説明文・提供元・URL は管理用でユーザーには見せない)。行のクリックで選ぶ。
 *
 * 行をクリックすると呼び出し側 (BgmSection) が `copy-to-materials` で組織の素材へ複製し、
 * シナリオの bgm_material_id に紐付ける (動画エディタの効果音ピッカーと同じ決定: シナリオの
 * BGM は素材参照しか持てないため。カタログ側が提供終了しても設定済みの BGM は壊れない)。
 * 紐付けに成功したらモーダルを閉じる。
 */

/** フリー楽曲として扱う既定用途 (audio_catalog_items.default_audio_role)。 */
export const FREE_BGM_AUDIO_ROLE = 'bgm';

/** カタログ一覧からフリー楽曲 (用途 bgm) だけを、パートナー独自 → 共通の順に分ける。 */
function splitFreeBgmTracks(items: AudioCatalogItem[]): {
  partnerItems: AudioCatalogItem[];
  commonItems: AudioCatalogItem[];
} {
  const tracks = items.filter((item) => item.default_audio_role === FREE_BGM_AUDIO_ROLE);
  return {
    partnerItems: tracks.filter((item) => item.scope === 'partner'),
    commonItems: tracks.filter((item) => item.scope !== 'partner'),
  };
}

interface PickerCommonProps {
  /** 採用中 BGM の複製元カタログ項目 id (「選択中」表示)。 */
  selectedCatalogId: string | null;
  /** 複製・紐付け処理中のカタログ項目 id (その行を「設定中…」にし、他行も押せなくする)。 */
  busyCatalogId: string | null;
  /** BGM 生成中など、選択自体を止めたいとき。 */
  disabled?: boolean;
  /** 選択の確定。true を返したらモーダルを閉じる (失敗時は開いたまま・トーストは呼び出し側)。 */
  onPick: (item: AudioCatalogItem) => Promise<boolean> | boolean | void;
}

interface BgmFreeTrackPickerModalProps extends PickerCommonProps {
  onClose: () => void;
}

/** 楽曲一覧のモーダル。 */
export function BgmFreeTrackPickerModal({
  selectedCatalogId,
  busyCatalogId,
  disabled = false,
  onPick,
  onClose,
}: BgmFreeTrackPickerModalProps) {
  const { data: allItems = [], isLoading, isError } = useAudioCatalog(true);
  const [query, setQuery] = useState('');

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // 紐付け中は閉じない (結果が分からなくなる)
      if (e.key === 'Escape' && busyCatalogId == null) onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose, busyCatalogId]);

  const { partnerItems, commonItems } = useMemo(() => {
    const groups = splitFreeBgmTracks(allItems);
    const q = query.trim().toLowerCase();
    if (!q) return groups;
    const match = (item: AudioCatalogItem) => item.name.toLowerCase().includes(q);
    return {
      partnerItems: groups.partnerItems.filter(match),
      commonItems: groups.commonItems.filter(match),
    };
  }, [allItems, query]);
  const totalTracks = useMemo(() => {
    const g = splitFreeBgmTracks(allItems);
    return g.partnerItems.length + g.commonItems.length;
  }, [allItems]);
  const shown = partnerItems.length + commonItems.length;
  const showGroupLabels = partnerItems.length > 0 && commonItems.length > 0;

  const handlePick = async (item: AudioCatalogItem) => {
    const ok = await onPick(item);
    if (ok === true) onClose();
  };

  // 曲名だけを出す。説明文は管理用メモで、ユーザー画面には表示しない (2026-09-24 ユーザー決定)。
  // 提供元名も効果音ピッカーと同じく出さない。
  // 出どころは見出しで区切らず「ライブラリ」列で出す (両方あるときだけ。パートナー独自を先に並べる)
  const toRow = (item: AudioCatalogItem, library: string | null): AudioTrackRow => {
    const selected = item.id === selectedCatalogId;
    const busy = item.id === busyCatalogId;
    const playable = !!item.preview_url;
    return {
      id: item.id,
      name: item.name,
      library,
      durationSeconds: item.duration_seconds,
      previewUrl: item.preview_url,
      selected,
      status: busy ? '設定中…' : selected ? '選択中' : null,
      disabled: disabled || busyCatalogId != null || !playable || selected,
      disabledReason: !playable ? '現在この楽曲は利用できません' : selected ? 'この楽曲をBGMに設定済みです' : undefined,
      onSelect: () => void handlePick(item),
    };
  };
  const rows: AudioTrackRow[] = [
    ...partnerItems.map((item) => toRow(item, showGroupLabels ? `${brandConfig.serviceName}オリジナル` : null)),
    ...commonItems.map((item) => toRow(item, showGroupLabels ? '共通ライブラリ' : null)),
  ];

  return createPortal(
    <div
      className="modal-overlay"
      onMouseDown={() => { if (busyCatalogId == null) onClose(); }}
      role="dialog"
      aria-modal="true"
      aria-label="フリー楽曲を選ぶ"
    >
      <div className="modal-card modal-card--md" onMouseDown={(e) => e.stopPropagation()}>
        <header className="modal-header">
          <h2 className="modal-title">フリー楽曲を選ぶ</h2>
          <button
            type="button"
            className="modal-close-button"
            onClick={onClose}
            disabled={busyCatalogId != null}
            aria-label="閉じる"
          >
            ✕
          </button>
        </header>
        <div className="modal-body">
          {!isLoading && !isError && totalTracks > 0 && (
            <input
              type="search"
              className={styles.search}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="曲名で絞り込み"
              aria-label="曲名で絞り込み"
            />
          )}
          {isLoading && <AudioTrackTableMessage>読み込み中…</AudioTrackTableMessage>}
          {isError && <AudioTrackTableMessage>楽曲一覧の取得に失敗しました</AudioTrackTableMessage>}
          {!isLoading && !isError && totalTracks === 0 && (
            <AudioTrackTableMessage>フリー楽曲はまだありません</AudioTrackTableMessage>
          )}
          {!isLoading && !isError && totalTracks > 0 && shown === 0 && (
            <AudioTrackTableMessage>「{query.trim()}」に一致する楽曲はありません</AudioTrackTableMessage>
          )}
          {!isLoading && !isError && shown > 0 && (
            <AudioTrackTable nameLabel="曲名" rowTestId="bgm-free-track-row" rows={rows} />
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

interface BgmFreeTrackPickerProps extends PickerCommonProps {
  /** 採用中 BGM の表示名 (カタログ由来でないときは null)。 */
  selectedName: string | null;
  selectedDurationSeconds: number | null;
}

/**
 * BGM セクション内のインライン部分。選択中の曲と「楽曲を選ぶ」ボタンだけを出し、
 * 一覧は BgmFreeTrackPickerModal に委ねる。
 */
export function BgmFreeTrackPicker({
  selectedCatalogId,
  selectedName,
  selectedDurationSeconds,
  busyCatalogId,
  disabled = false,
  onPick,
}: BgmFreeTrackPickerProps) {
  const [open, setOpen] = useState(false);
  const hasSelection = !!selectedCatalogId && !!selectedName;

  return (
    <div className={styles.panel} onClick={(e) => e.stopPropagation()}>
      <div>
        <div className={styles.panelLead}>用意された楽曲から BGM を選んでください</div>
        <div className={styles.panelSub}>ポイントは消費しません。選ぶとすぐに BGM として設定されます</div>
      </div>
      <div className={styles.currentRow}>
        <span className={styles.currentLabel}>選択中</span>
        {hasSelection ? (
          <span className={styles.currentName} title={selectedName ?? undefined}>
            {selectedName}
            {selectedDurationSeconds ? `（${formatAudioDuration(selectedDurationSeconds)}）` : ''}
          </span>
        ) : (
          <span className={styles.currentEmpty}>まだ選ばれていません</span>
        )}
        <button
          type="button"
          className={sectionStyles.storyboardUploadTileButton}
          style={{ fontSize: '13px' }}
          disabled={disabled}
          onClick={() => setOpen(true)}
        >
          {hasSelection ? '別の楽曲を選ぶ' : '楽曲を選ぶ'}
        </button>
      </div>
      {open && (
        <BgmFreeTrackPickerModal
          selectedCatalogId={selectedCatalogId}
          busyCatalogId={busyCatalogId}
          disabled={disabled}
          onPick={onPick}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  );
}
